import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Document, PaginatedResponse, ApiError } from '../../types';
import { RootState } from '@store/index';

interface DocumentsState {
  documents: Document[];
  selectedDocument: Document | null;
  selectedDocuments: string[];
  total: number;
  page: number;
  pageSize: number;
  isLoading: boolean;
  uploadProgress: number;
  error: string | null;
  viewMode: 'list' | 'grid';
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  filters: {
    category?: string;
    status?: string;
    tags?: string[];
    search?: string;
  };
}

const initialState: DocumentsState = {
  documents: [],
  selectedDocument: null,
  selectedDocuments: [],
  total: 0,
  page: 1,
  pageSize: 20,
  isLoading: false,
  uploadProgress: 0,
  error: null,
  viewMode: 'list',
  sortBy: 'updatedAt',
  sortOrder: 'desc',
  filters: {},
};

// Async thunks will be implemented when API endpoints are ready
export const fetchDocuments = createAsyncThunk<
  PaginatedResponse<Document>,
  { page: number; pageSize: number; category?: string },
  { rejectValue: ApiError }
>('documents/fetchDocuments', async (_params, { rejectWithValue }) => {
  try {
    // TODO: Implement API call
    throw new Error('Not implemented');
  } catch (error) {
    return rejectWithValue(error as ApiError);
  }
});

// Slice
const documentsSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {
    setSelectedDocument: (state, action: PayloadAction<Document | null>) => {
      state.selectedDocument = action.payload;
    },
    setUploadProgress: (state, action: PayloadAction<number>) => {
      state.uploadProgress = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetUploadProgress: (state) => {
      state.uploadProgress = 0;
    },
    setViewMode: (state, action: PayloadAction<'list' | 'grid'>) => {
      state.viewMode = action.payload;
    },
    setSortBy: (state, action: PayloadAction<string>) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.sortOrder = action.payload;
    },
    toggleDocumentSelection: (state, action: PayloadAction<string>) => {
      const documentId = action.payload;
      const index = state.selectedDocuments.indexOf(documentId);
      if (index === -1) {
        state.selectedDocuments.push(documentId);
      } else {
        state.selectedDocuments.splice(index, 1);
      }
    },
    selectAllDocuments: (state, action: PayloadAction<string[]>) => {
      state.selectedDocuments = action.payload;
    },
    clearDocumentSelection: (state) => {
      state.selectedDocuments = [];
    },
    setFilters: (state, action: PayloadAction<DocumentsState['filters']>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDocuments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDocuments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.documents = action.payload.results;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.pageSize = action.payload.pageSize;
      })
      .addCase(fetchDocuments.rejected, (state, action) => {
        state.isLoading = false;
        // The code is the contract; the server's prose is a log artifact and
        // must not become UI text, so it never enters state.
        state.error = action.payload?.code || 'DOCUMENTS_FETCH_FAILED';
      });
  },
});

// Actions
export const {
  setSelectedDocument,
  setUploadProgress,
  clearError,
  resetUploadProgress,
  setViewMode,
  setSortBy,
  setSortOrder,
  toggleDocumentSelection,
  selectAllDocuments,
  clearDocumentSelection,
  setFilters,
  clearFilters,
} = documentsSlice.actions;

// Selectors
export const selectDocuments = (state: RootState) => state.documents.documents;
export const selectSelectedDocument = (state: RootState) => state.documents.selectedDocument;
export const selectSelectedDocuments = (state: RootState) => state.documents.selectedDocuments;
export const selectDocumentsLoading = (state: RootState) => state.documents.isLoading;
export const selectUploadProgress = (state: RootState) => state.documents.uploadProgress;
export const selectDocumentsError = (state: RootState) => state.documents.error;
export const selectViewMode = (state: RootState) => state.documents.viewMode;
export const selectSortBy = (state: RootState) => state.documents.sortBy;
export const selectSortOrder = (state: RootState) => state.documents.sortOrder;
export const selectFilters = (state: RootState) => state.documents.filters;

export default documentsSlice.reducer;
